// Novel V3B: isolated, read-only physical layout study.
// The dimensions are a frozen planning mirror of the six paperback entries
// in backend/utils/bookProductionTrimRegistry.js. Tests enforce registry parity.
// These are trim dimensions, NOT bleed PDF bounds or an approved print profile.
const trimDimensions = Object.freeze({
  "5.5_x_8.5_in": Object.freeze({ key: "5.5x8.5", width: 5.5, height: 8.5, orientation: "portrait" }),
  "6_x_9_in": Object.freeze({ key: "6x9", width: 6, height: 9, orientation: "portrait" }),
  "8.25_x_8.25_in": Object.freeze({ key: "8.25x8.25", width: 8.25, height: 8.25, orientation: "square" }),
  "8.5_x_8.5_in": Object.freeze({ key: "8.5x8.5", width: 8.5, height: 8.5, orientation: "square" }),
  "8_x_10_in": Object.freeze({ key: "8x10", width: 8, height: 10, orientation: "portrait" }),
  "8.5_x_11_in": Object.freeze({ key: "8.5x11", width: 8.5, height: 11, orientation: "portrait" }),
});

// Shared Textbook classic design and trim-neutral typography reference.
// They are a visual *study* here, not final production margins, typesetting
// authority, a page-count-sensitive gutter, or a page-count measurement.
export const NOVEL_V3B_CLASSIC_MARGINS = Object.freeze({
  inside: 0.75, outside: 0.5, top: 0.75, bottom: 0.75,
});
export const NOVEL_V3B_CLASSIC_BODY = Object.freeze({
  family: "Georgia", fontSizePt: 11, lineHeightPt: 14, paragraphAfterPt: 6,
});

const clean = (v) => String(v ?? "").trim();
const rows = (v) => Array.isArray(v) ? v : [];
const lowered = (v) => clean(v).toLocaleLowerCase("en");
const isTitle = (v) => /^(title|book title|title page|titre|page de titre)$/.test(lowered(v));
const isSubtitle = (v) => /^(subtitle|sub-title|sous-titre)$/.test(lowered(v));
const isCopyright = (v) => /^(copyright|copyright page|droits d'auteur|droits d’auteur)$/.test(lowered(v));

// This is a *preview text* helper, not the source for production rendering.
// Preserve the entire manuscriptContent object on each matter unit below.
function savedMatterText(item) {
  if (!item || typeof item !== "object") return "";
  const blocks = rows(item.manuscriptContent?.blocks);
  const savedRich = blocks.map((block) => {
    const runs = rows(block?.runs).map((run) => String(run?.text ?? "")).join("");
    return clean(runs) || clean(block?.text);
  }).filter(Boolean).join("\n");
  return clean(savedRich) || clean(item.text);
}

function indexMatter(items) {
  const byId = new Map();
  rows(items).forEach((item, index) => {
    const id = clean(item?.id);
    // Duplicate IDs are not silently collapsed. The unlinked record is
    // identified by the coverage check and the source manifest flags it.
    if (id && !byId.has(id)) byId.set(id, { item, index });
  });
  return byId;
}

function roleForMatter(kind, title) {
  if (kind === "chapter") return "chapter";
  if (kind === "front") {
    if (isTitle(title)) return "title_only";
    if (isSubtitle(title)) return "subtitle_author";
    if (isCopyright(title)) return "copyright";
    return "front_matter";
  }
  if (kind === "back") return "back_matter";
  return "unknown";
}

export function buildNovelInteriorPhysicalLayoutV3B({
  bookFormat = {}, interiorPlan = {}, bookTitle = "", authorName = "",
  frontMatter = [], backMatter = [],
} = {}) {
  const format = bookFormat && typeof bookFormat === "object" ? bookFormat : {};
  const trim = Object.prototype.hasOwnProperty.call(trimDimensions, clean(format.trimSize))
    ? trimDimensions[clean(format.trimSize)] : null;
  const orientationCompatible = Boolean(trim && trim.orientation === clean(format.orientation));
  const savedFront = rows(frontMatter);
  const savedBack = rows(backMatter);
  const frontById = indexMatter(savedFront);
  const backById = indexMatter(savedBack);
  const linkedFront = new Set();
  const linkedBack = new Set();
  let unlinkedPlanMatterCount = 0;

  // V3A is derived from the complete saved-source inventory, not a fixed
  // Title/Subtitle/Copyright or Author Notes/About Author template.
  const units = rows(interiorPlan.units).map((u) => {
    const kind = clean(u?.kind);
    const sourceId = clean(u?.sourceId);
    const sourceIndex = kind === "front" ? frontById
      : kind === "back" ? backById : null;
    const link = sourceIndex?.get(sourceId) || null;
    if (kind === "front" || kind === "back") {
      if (!link) unlinkedPlanMatterCount += 1;
      else (kind === "front" ? linkedFront : linkedBack).add(link.index);
    }
    // Carry the original full, structured saved matter record through this
    // read-only study. Do not flatten or discard runs, images, formatting,
    // paragraph order, metadata or additional fields. Nothing is mutated.
    const sourceRecord = link?.item || null;
    return {
      sourceId,
      position: u?.position,
      kind,
      title: clean(u?.title),
      startPolicy: "next_page",
      role: roleForMatter(kind, u?.title),
      savedMatterLinked: Boolean(link),
      sourceRecord, // original read-only reference for eventual source adapter
      sourceContent: sourceRecord?.manuscriptContent || null,
      sourcePlainText: sourceRecord ? String(sourceRecord.text ?? "") : "",
      measuredStartPage: null,
      measuredPageCount: null,
    };
  });

  const unlistedSavedMatterCount =
    (savedFront.length - linkedFront.size) + (savedBack.length - linkedBack.size);
  const sourceBindingsComplete = unlinkedPlanMatterCount === 0 &&
    unlistedSavedMatterCount === 0;
  const titlePages = units.filter((u) => u.role === "title_only");
  const subtitlePages = units.filter((u) => u.role === "subtitle_author");
  const copyrightPages = units.filter((u) => u.role === "copyright");
  const subtitleSource = subtitlePages[0]?.sourceRecord || null;
  const margin = NOVEL_V3B_CLASSIC_MARGINS;
  const contentWidth = trim ? Number((trim.width - margin.inside - margin.outside).toFixed(3)) : null;
  const contentHeight = trim ? Number((trim.height - margin.top - margin.bottom).toFixed(3)) : null;

  return {
    schemaVersion: "novel-interior-physical-layout-v3b",
    stage: "read_only_measurement_study",
    geometrySupported: Boolean(orientationCompatible && contentWidth > 0 && contentHeight > 0),
    printChoicesCompatible: interiorPlan.planningInputsCompatible === true,
    trimKey: orientationCompatible ? trim.key : null,
    trimWidthInches: orientationCompatible ? trim.width : null,
    trimHeightInches: orientationCompatible ? trim.height : null,
    studyMarginsInches: orientationCompatible ? { ...margin } : null,
    studyContentWidthInches: orientationCompatible ? contentWidth : null,
    studyContentHeightInches: orientationCompatible ? contentHeight : null,
    studyTypography: { ...NOVEL_V3B_CLASSIC_BODY },
    units,
    savedFrontMatterCount: savedFront.length,
    savedBackMatterCount: savedBack.length,
    unlinkedPlanMatterCount,
    unlistedSavedMatterCount,
    sourceBindingsComplete,
    titlePageCount: titlePages.length,
    subtitlePageCount: subtitlePages.length,
    copyrightPageCount: copyrightPages.length,
    distinctTitleAndSubtitle: titlePages.length === 1 && subtitlePages.length === 1 &&
      titlePages[0].sourceId !== subtitlePages[0].sourceId,
    titlePageText: titlePages.length ? clean(bookTitle) : "",
    subtitlePageText: savedMatterText(subtitleSource),
    authorByline: subtitlePages.length ? clean(authorName) : "",
    sourceUnchanged: true,
    productionMarginsApproved: false,
    productionTypographyApproved: false,
    physicalPagesMeasured: false,
    measuredInteriorPdf: null,
    finalPageCount: null,
    finalPageCountLocked: false,
    manuscriptLocked: false,
    spineWidthInches: null,
    publicationEnabled: false,
    exportEnabled: false,
  };
}
